import React from "react";
import { LoaderFunctionArgs, useParams } from "react-router-dom";

import { ADMIN_ROLES } from "@performyard/utils-isomorphic";

import { useCurrentUser, useMeetingWorkspaceDetails } from "../../../api/hooks";
import type { SearchServiceSort } from "../../../api/hooks";
import { getFeatureFlagQuery, useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";
import { useWorkspaceNavigationState } from "./hooks";

import { currentUserQuery, meetingWorkspaceDetailsQuery, meetingsSearchQuery } from "../../../api/queries";
import { SimplifiedQuery, dataLoader } from "../../../api/queries/dataLoader";
import { queryClient } from "../../../api/queryClient";
import type { MeResponse } from "../../../api/services";

import { convertFiltersToSearchParams } from "./utils";

import ErrorPage from "../../Error";
import AdminWorkspaceDetails from "./AdminView/AdminWorkspaceDetails";
import ParticipantWorkspaceDetails from "./ParticipantView/ParticipantWorkspaceDetails";
import { config } from "./config";

// Shared data loader function for both admin and regular views
export const meetingWorkspaceDetailsLoader = ({ params, request }: LoaderFunctionArgs) => {
	const workspaceId = params.workspaceId;

	const queries: (() => SimplifiedQuery)[] = [];

	if (workspaceId) {
		queries.push(
			() => meetingWorkspaceDetailsQuery(workspaceId),
			() => getFeatureFlagQuery(),
			() => {
				/** Search service query */
				// acting user ID is required for meetings search
				const currentUser = queryClient.getQueryData(["auth-current-user"]) as MeResponse;
				const userId = currentUser?.me?.authorization?.payload?.user_id || "";

				// Get URL search parameters from request
				const url = new URL(request.url);
				const searchParams = url.searchParams;

				// Get page number using the same logic as useInfoPageTemplateQueryState
				const pageNumber = Number(searchParams.get("pageNumber")) || config.defaultPageNumber;
				const sort: SearchServiceSort = {
					name: searchParams.get("name") || config.defaultSortName,
					direction: (searchParams.get("direction") || config.defaultSortDirection) as "asc" | "desc",
				};

				// Parse filters with error handling
				let filters = {};
				const filtersParam = searchParams.get("filters");
				if (filtersParam) {
					try {
						filters = JSON.parse(filtersParam);
					} catch (error) {
						console.warn("Failed to parse filters from URL search parameters:", error);
						filters = {};
					}
				}

				if (Object.keys(filters).length > 0) {
					// convert all date strings to date objects
					// we can find dates by looking for the string "date-" at the beginning of the value
					for (const key in filters) {
						if (typeof filters[key] === "string" && filters[key].startsWith("date-")) {
							filters[key] = new Date(filters[key].replace("date-", "")) as Date;
						}
					}
				}

				// Use admin-compatible defaults that work for both views
				// Admin view always uses privateNotes: "any", so we default to that
				// Regular users can still override via URL params if needed
				const finalFilters = {
					...config.fallbackFilters,
					privateNotes: "any" as "any" | "has-private-notes" | "no-private-notes", // Admin-compatible default
					...filters,
				};

				// If private notes filter is applied but userId is empty, skip the private notes filter
				// to prevent backend from silently ignoring it and returning incomplete results
				if (finalFilters.privateNotes && finalFilters.privateNotes !== "any" && !userId) {
					console.warn("Private notes filter requires a valid user ID. Filter will be ignored.");
					finalFilters.privateNotes = "any";
				}

				return meetingsSearchQuery(
					convertFiltersToSearchParams(finalFilters, sort, pageNumber, workspaceId, userId)
				);
			}
		);
	}

	return dataLoader([[currentUserQuery], queries]);
};

const WorkspaceDetailsRouter = () => {
	const { workspaceId } = useParams();

	const { data: currentUser } = useCurrentUser();
	const { data: workspace } = useMeetingWorkspaceDetails(workspaceId || "");
	const { value: ffMeetings } = useFeatureFlagQuery("ff-is-meetings-enabled", false);

	// Show access denied page if feature flag is disabled
	if (!ffMeetings) {
		return <ErrorPage code={403} />;
	}

	// Show error if no workspace found
	if (!workspace) {
		return <ErrorPage code={404} />;
	}

	const userId = currentUser?.me?.authorization?.payload.user_id || "";
	const userRole = currentUser?.me?.authorization?.payload.user_primary_role || "";

	// Check if user is an admin (billing-admin, admin, or basic-admin)
	const isAdmin = ADMIN_ROLES.includes(userRole);

	// Check if user is a participant in this workspace
	const isParticipant = workspace.participants?.some((participant) => participant.userId === userId);

	// Check if user has access - must be either admin OR participant
	const hasAccess = isAdmin || isParticipant;

	if (!hasAccess) {
		return <ErrorPage code={403} />;
	}

	// Update main navigation active state based on view type
	useWorkspaceNavigationState({ isAdmin, isParticipant });

	// If user is an admin AND not a participant, show admin view
	// If user is an admin AND a participant, show regular view (same permissions as accessing through meetings module)
	// If user is not an admin, show regular view (if they have access)
	if (isAdmin && !isParticipant) {
		return <AdminWorkspaceDetails />;
	} else {
		return <ParticipantWorkspaceDetails />;
	}
};

export default WorkspaceDetailsRouter;
